import React, { useState, useEffect, useRef } from 'react';
import './LazyImage.css';

export const LazyImage = ({ src, alt, style, className, ...imgProps }) => {
    const [isVisible, setIsVisible] = useState(false);
    const [hasLoaded, setHasLoaded] = useState(false);
    const [hasError, setHasError] = useState(false);
    const containerRef = useRef(); // Attach observer to the container
    const startedAtRef = useRef(Date.now());
    const minShimmerMs = 350;

    useEffect(() => {
        // Reset state when the source changes so the shimmer appears for new images.
        setHasLoaded(false);
        setHasError(false);
        startedAtRef.current = Date.now();
    }, [src]);

    useEffect(() => {
        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting) {
                    setIsVisible(true);
                    observer.disconnect();
                }
            },
            { threshold: 0.1 }
        );

        // Fallback: if observer misses due to route transitions/layout timing,
        // still allow the image request to start.
        const forceVisibleTimer = setTimeout(() => {
            setIsVisible(true);
        }, 1000);

        if (containerRef.current) {
            observer.observe(containerRef.current);
        }

        return () => {
            clearTimeout(forceVisibleTimer);
            if (containerRef.current) {
                observer.disconnect();
            }
        };
    }, [src]);

    return (
        <div
            ref={containerRef}
            className={className}
            style={{
                position: 'relative',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                width: '100%',
                minHeight: '120px',
                overflow: 'hidden', // Prevents skewing or overflow
                ...style,
            }}
        >
            {/* Shimmer placeholder */}
            {!hasLoaded && !hasError && (
                <div className="lazy-placeholder" />
            )}
            {/* Actual Image */}
            {isVisible && !hasError && (
                <img
                    src={src}
                    alt={alt}
                    onLoad={() => {
                        const elapsed = Date.now() - startedAtRef.current;
                        const delay = Math.max(0, minShimmerMs - elapsed);
                        setTimeout(() => setHasLoaded(true), delay);
                    }}
                    onError={(e) => {
                        setHasError(true);
                        console.error(`Failed to load image: ${src}`, e); // Debugging log
                    }}
                    style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                        opacity: hasLoaded ? 1 : 0, // Fade in the actual image
                        transition: 'opacity 0.3s ease-in-out',
                    }}
                    {...imgProps}
                />
            )}
        </div>
    );
};